import React from "react";
import classNames from "classnames";

import { Context } from "../../../../../context";
import { useProjectCtx } from "../../../../../projectCtx";
import { useConstraints } from "../../../../constraintsOverlay";
import { isMilestone, isTask } from "../../utils";
import { Props } from "./types";
import { Icon } from "./icon";
import styles from "./styles.scss";

function isInRect(x: number, y: number, rect: DOMRect) {
  return y > rect.top && y < rect.bottom && x > rect.left && x < rect.right;
}

function Handle({ element, constraints, direction }: Props) {
  const isLeft = direction === "left";
  const isRight = direction === "right";
  const { cell } = React.useContext(Context);
  const { project } = useProjectCtx();
  const ref = React.useRef<HTMLDivElement>(null);
  const {
    x,
    y,
    origin,
    target,
    targetDateType,
    originDateType,
    startDrag,
    dragInElement,
    dragOutElement,
  } = useConstraints();
  const isAlreadyConstrained = !!constraints.find(
    (constraint) =>
      isLeft &&
      constraint.targetEntityUuid === element.uuid &&
      constraint.targetDateType === "START",
  );
  const isActionPossible =
    (origin && originDateType === "START" ? isLeft : true) &&
    (isTask(element) || isMilestone(element));
  const canConstrain = !isAlreadyConstrained && isActionPossible;

  React.useEffect(() => {
    if (ref.current && origin && canConstrain) {
      const rect = ref.current.getBoundingClientRect();

      if (
        isInRect(x, y, {
          ...rect,
          top: rect.top,
          bottom: rect.bottom,
          left: isLeft ? rect.left : rect.left - cell.width,
          right: isRight ? rect.right : rect.right + cell.width,
        })
      ) {
        element.uuid !== origin.uuid &&
          dragInElement({
            element,
            dateType: isLeft ? "START" : "END",
            snapPoint: {
              y: rect.top + (cell.height - 10) / 2,
              x: isLeft
                ? rect.left + (cell.height - 15) / 2
                : rect.right - (cell.height - 15) / 2,
            },
          });
      } else if (
        target &&
        target.uuid === element.uuid &&
        targetDateType === (isLeft ? "START" : "END")
      ) {
        dragOutElement({ element, dateType: isLeft ? "START" : "END" });
      }
    }
  }, [x, y, origin, target, targetDateType, canConstrain]);

  return canConstrain ? (
    <div
      ref={ref}
      className={classNames(styles.handle, {
        [styles.rightHandler]: isRight,
        [styles.leftHandler]: isLeft,
      })}
      style={{
        width: cell.height - 15,
        height: cell.height - 10,
        ...(isRight
          ? { left: `calc(100% + ${cell.height}px)` }
          : { right: `calc(100% + ${cell.height}px)` }),
      }}
      onMouseDown={(e) => {
        startDrag({
          x: e.clientX,
          y: e.clientY,
          element,
          project,
          dateType: isLeft ? "START" : "END",
        });
      }}
      onTouchStart={(e) => {
        e.changedTouches.length > 0 &&
          startDrag({
            x: e.changedTouches[0].clientX,
            y: e.changedTouches[0].clientY,
            element,
            project,
            dateType: isLeft ? "START" : "END",
          });
      }}
    >
      <Icon className={styles.icon} />
    </div>
  ) : (
    <div />
  );
}

export { Handle };
